import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { PascoDTO } from '@src/application/pascos/dto';

/**
 * Hook: useGetPascoById
 * Fetches a specific PASCO by its ID using React Query
 * 
 * @param pascoId - The ID of the PASCO to fetch
 * @param options - React Query options
 * @returns Query result with the PASCO DTO or null
 * 
 * @example
 * ```typescript
 * const { data: pasco, isLoading, error } = useGetPascoById('pasco-123');
 * ```
 */
export function useGetPascoById(
    pascoId: string,
    options?: Omit<UseQueryOptions<PascoDTO | null, Error>, 'queryKey' | 'queryFn'>
) {
    const pascoService = getPascoService();

    return useQuery({
        queryKey: ['pascos', pascoId],
        queryFn: () => pascoService.getById(pascoId),
        enabled: !!pascoId, // Only run query if pascoId is provided
        staleTime: 1 * 60 * 1000, // 1 minute
        refetchInterval: 0, // Disable automatic refetching
        retry: false, // Disable retries on failure
        ...options,
    });
}
